/** @jsx jsx */
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';

import { challengeSelector } from 'reducers/challenge.reducer';
import { ChallengeDatesType, ChallengePhaseType } from 'interfaces';
import { useImgSrc } from 'core/utils/use-img-src.hook';
import { getChallengePhase } from 'core/utils/challenges.helpers';
import { getFormatedDates } from 'core/utils/date.helpers';
import {
  Container,
  PageContainer,
} from 'components/ChallengeDetailsContainers';
import {
  AccessDenied,
  ChallengeDescription,
  ChallengeDescriptionSection,
  ChallengeGoalsAndRewards,
  ChallengeResults,
  ChallengeRankList,
  ChallengeResultsSection,
  Banner,
} from 'components';
import { CHALLENGE_PHASE, GOAL, CHALLENGE } from '../../../../constants';

const DetailsChallengeDefault = ({
  challengeId,
}: {
  challengeId: number;
}): JSX.Element => {
  const {
    i18n: { language },
  } = useTranslation();
  const { data, status: challengeStatus } = useSelector(
    challengeSelector(challengeId),
  );

  /*
      The group concept is linked to 'Defis' challenges only
      A user can have several results/goals depending on his groups
      A single result/goal is displayed but a system of tabs allows you to change your choice
  */
  const [currentGroupTab, setCurrentGroupTab] = useState(0);

  const {
    areResultsAnonymized,
    areResultsVisible,
    challengeGoals,
    challengeResults: userChallengeResults,
    conditions,
    endDate,
    endDiffusionDate,
    endSignInDate,
    goalType,
    image: challengeImage,
    isGroup,
    resultUnit,
    startDate,
    startDiffusionDate,
    startSignInDate,
    type: challengeType,
  } = data || {};
  const { fullUri } = useImgSrc(challengeImage) || {};
  const isParticipating: boolean | undefined =
    challengeType === CHALLENGE.CLASSIC ||
    userChallengeResults?.[0]?.isParticipating;

  useEffect(() => {
    if (isGroup && userChallengeResults) {
      const ranks = userChallengeResults.map((result) => result.rank);
      const minRank = Math.min(...ranks);
      const bestGroupIndex = ranks.indexOf(minRank);
      setCurrentGroupTab(bestGroupIndex === -1 ? 0 : bestGroupIndex);
    }
  }, [isGroup, userChallengeResults]);

  const handleChangeGroupTab = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentGroupTab(value);
  };

  const dates: ChallengeDatesType = getFormatedDates(
    {
      challenge: {
        start: startDate,
        end: endDate,
      },
      diffusion: {
        start: startDiffusionDate,
        end: endDiffusionDate,
      },
      signIn: {
        start: startSignInDate,
        end: endSignInDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );

  const currentPhase = getChallengePhase(dates);
  const errorPhases: (ChallengePhaseType | null)[] = [
    CHALLENGE_PHASE.BEFORE_DIFFUSION,
    CHALLENGE_PHASE.AFTER_DIFFUSION,
  ];
  const isChallengeVisible = !errorPhases.includes(currentPhase);
  const hasChallengeStarted =
    currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE ||
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;

  const showGoalsAndRewards: boolean = useMemo((): boolean => {
    const isGenericOrCustomGoalType =
      goalType === GOAL.GENERIC || goalType === GOAL.CUSTOM;

    if (!isGenericOrCustomGoalType) {
      return false;
    }
    if (challengeType === CHALLENGE.DEAL) {
      return (
        isParticipating ||
        currentPhase === CHALLENGE_PHASE.BEFORE_SIGN_IN ||
        currentPhase === CHALLENGE_PHASE.DURING_SIGN_IN
      );
    }

    return challengeType !== CHALLENGE.PHOTO;
  }, [currentPhase, challengeType, isParticipating, goalType]);

  if (!isChallengeVisible) {
    return <AccessDenied />;
  }

  const userChallengeResult = userChallengeResults?.[currentGroupTab ?? 0];
  const hasResult = userChallengeResult?.resultValue != null;

  return (
    <Container>
      <Banner
        challengeType={challengeType}
        dates={dates}
        half={false}
        image={fullUri}
        isParticipating={isParticipating}
      />

      <PageContainer>
        <ChallengeDescriptionSection>
          <ChallengeDescription
            challengeStatus={challengeStatus}
            challenge={data}
            challengeResults={userChallengeResults?.[0]}
            currentPhase={currentPhase}
            isParticipating={isParticipating}
            personalChallengeGoals={challengeGoals?.[0]}
          />
        </ChallengeDescriptionSection>
        {data && showGoalsAndRewards && (
          <ChallengeGoalsAndRewards
            challengeResults={userChallengeResults}
            challengeStatus={challengeStatus}
            conditions={conditions}
            currentPhase={currentPhase}
            currentGroupTab={currentGroupTab}
            goals={challengeGoals}
            goalType={goalType}
            isGroup={isGroup}
            onChangeTab={handleChangeGroupTab}
            isParticipating={isParticipating}
            challengeType={challengeType}
          />
        )}
        {hasChallengeStarted && isParticipating && (
          <React.Fragment>
            <ChallengeResultsSection
              hasResult={hasResult}
              date={userChallengeResult?.createdAt}
            >
              <ChallengeResults
                areResultsVisible={areResultsVisible}
                currentGroupTab={currentGroupTab}
                disabled={
                  currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE &&
                  !userChallengeResults?.length
                }
                goals={challengeGoals}
                goalType={goalType}
                unitToDisplay={resultUnit}
                isGroup={isGroup}
                onChangeTab={handleChangeGroupTab}
                userChallengeResults={userChallengeResults}
              />
            </ChallengeResultsSection>
            {areResultsVisible && (
              <ChallengeRankList
                areResultsAnonymized={areResultsAnonymized}
                challengeType={challengeType}
                goals={challengeGoals}
                goalType={goalType}
                isGroup={isGroup}
                resultUnit={resultUnit}
              />
            )}
          </React.Fragment>
        )}
      </PageContainer>
    </Container>
  );
};

export default DetailsChallengeDefault;
